import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { formatISO, parseISO } from "date-fns";
import isEqual from "lodash.isequal";
import cloneDeep from "lodash.clonedeep";
import { OrderService, PreOrderService } from "../../../../services";
import { showErrorToast } from "../../../../components";
import { HttpService } from "../../../../services/http";
import { ITEMS_PER_PAGE } from "../../../../constants/Paginate";
import { USER_ROLES } from "../../../../constants";
import { getNumberOfAppliedFilters } from "../../../../utils";
import {
  preOrderFieldNames,
  preOrderFiltersInitialState,
} from "./filters/constants";

const searchInitialState = {
  type: undefined,
  term: "",
};

const usePreOrderList = ({
  handleDownloadPreOrder,
  handleResetCancelState,
  handleResetApprovalState,
  userData,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { search: locationSearch } = useLocation();

  const [loading, setLoading] = useState(false);
  const [preOrderDetailData, setPreOrderDetailData] = useState({
    data: {},
    loading: true,
  });
  const [preOrderResponse, setPreOrderResponse] = useState(null);
  const [orderData, setOrderData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [search, setSearch] = useState(searchInitialState);
  const [appliedFilters, setAppliedFilters] = useState(
    preOrderFiltersInitialState
  );
  const [countAppliedFilters, setCountAppliedFilters] = useState(0);
  const [showFilterIndicator, setShowFilterIndicator] = useState(false);
  const [isCancelPreOrderDialog, setIsCancelPreOrderDialog] = useState({
    id: null,
    preOrderId: null,
    open: false,
  });
  const [authorList, setAuthorList] = useState({
    list: [],
    loading: false,
  });
  const [isApprovePreOrderDialog, setIsApprovePreOrderDialog] = useState({
    id: null,
    orderId: null,
    open: false,
  });

  const title: string = orderData[0]?.data?.receiver?.name || "";
  const canApprove: boolean = orderData[0]?.data?.canApprove;
  const canCancel: boolean = orderData[0]?.data?.canRefuse;
  const promotionalCicle: string =
    orderData[0]?.data?.promotionalCicle || "Avulso";
  const isAgent: boolean = userData.userRole === USER_ROLES.AGENT;
  const isEmployee: boolean = userData.userRole === USER_ROLES.EMPLOYEE;

  const updateQueryStringInPreOrderList = (
    skip,
    take,
    searchData = search,
    filters = appliedFilters
  ) => {
    const queryParams = {
      skip,
      take,
    };

    if (searchData && searchData.term && searchData.term.trim()) {
      queryParams.searchTerm = searchData.term;
      queryParams.searchType = searchData.type;
    }

    if (filters.createdAt?.from) {
      queryParams.createdAtFrom = formatISO(filters.createdAt.from, {
        representation: "date",
      });
    }
    if (filters.createdAt?.to) {
      queryParams.createdAtTo = formatISO(filters.createdAt.to, {
        representation: "date",
      });
    }

    queryParams.authorList = Object.keys(filters.authorList || {}).map((key) =>
      filters.authorList[key] ? key : null
    );

    queryParams.statusList = Object.keys(filters.statusList || {}).map((key) =>
      filters.statusList[key] ? key : null
    );

    history.push(`?${HttpService.serializeQueryString(queryParams)}`);
  };

  const buildRequestFromQueryString = () => {
    const queryObject = HttpService.unserializeQueryString(locationSearch);
    const skip = queryObject.skip ? parseInt(queryObject.skip, 10) : 0;
    const take = queryObject.take
      ? parseInt(queryObject.take, 10)
      : ITEMS_PER_PAGE;
    const currentPageFromUrl = Math.floor(skip / take) + 1;

    const searchData = {
      term: queryObject.searchTerm || "",
      type: queryObject.searchType,
    };

    const filters = {
      [preOrderFieldNames.CREATE_DATE]: {
        from: queryObject.createdAtFrom
          ? parseISO(queryObject.createdAtFrom)
          : null,
        to: queryObject.createdAtTo
          ? parseISO(queryObject.createdAtTo)
          : null,
      },
      [preOrderFieldNames.AUTHOR]: HttpService.unserializeQueryStringCheckboxObject(
        queryObject.authorList,
        null,
        {}
      ),
      [preOrderFieldNames.STATUS]: HttpService.unserializeQueryStringCheckboxObject(
        queryObject.statusList,
        Number,
        {}
      ),
    };

    return {
      skip,
      take,
      currentPage: currentPageFromUrl,
      search: searchData,
      filters,
    };
  };

  const _onShowFilterIndicator = (filters, searchData = search) => {
    setShowFilterIndicator(
      !isEqual(preOrderFiltersInitialState, filters) ||
        (searchData.term && searchData.term !== "")
    );
  };

  const fetchPreOrderDetail = async (
    preOrderId,
    paginationData = {},
    searchData = {},
    filters = preOrderFiltersInitialState
  ) => {
    try {
      setLoading(true);
      setPreOrderDetailData((prev) => ({ ...prev, loading: true }));

      const orderResponse = await OrderService.fetchOrderDetail(preOrderId);
      const orderDataResult = orderResponse.data;

      const paginationParams = {
        preOrderId,
        skip: paginationData.skip,
        take: paginationData.take,
      };

      const standardFilters = [];

      if (searchData && searchData.term && searchData.term.trim()) {
        standardFilters.push({
          attribute: searchData.type || "search",
          value: searchData.term,
        });
      }

      if (filters.createdAt?.from || filters.createdAt?.to) {
        if (filters.createdAt.from) {
          standardFilters.push({
            attribute: "createdAtFrom",
            value: formatISO(filters.createdAt.from, {
              representation: "date",
            }),
          });
        }
        if (filters.createdAt.to) {
          standardFilters.push({
            attribute: "createdAtTo",
            value: formatISO(filters.createdAt.to, {
              representation: "date",
            }),
          });
        }
      }

      const selectedAuthors = Object.keys(filters.authorList || {}).filter(
        (key) => filters.authorList[key]
      );
      if (selectedAuthors.length > 0) {
        standardFilters.push({
          attribute: "authorList",
          value: selectedAuthors,
        });
      }

      const selectedStatuses = Object.keys(filters.statusList || {}).filter(
        (key) => filters.statusList[key]
      );
      if (selectedStatuses.length > 0) {
        standardFilters.push({
          attribute: "statusList",
          value: selectedStatuses,
        });
      }

      if (standardFilters.length > 0) {
        paginationParams.filters = { standard: standardFilters };
      }

      const response = await PreOrderService.fetchPreOrderDetail(
        paginationParams
      );

      setPreOrderDetailData({
        data: orderDataResult,
        loading: false,
      });

      setPreOrderResponse(response);
      setOrderData([{ data: orderDataResult }]);
    } catch (error) {
      setPreOrderDetailData({
        data: {},
        loading: false,
      });
      setPreOrderResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const _onReloadDataTable = (filters, searchData, skip, take) => {
    setCountAppliedFilters(
      getNumberOfAppliedFilters(preOrderFiltersInitialState, filters)
    );
    _onShowFilterIndicator(filters, searchData);
    fetchPreOrderDetail(id, { skip, take }, searchData, filters);
  };

  useEffect(() => {
    const {
      skip,
      take,
      currentPage: urlCurrentPage,
      search: urlSearch,
      filters: urlFilters,
    } = buildRequestFromQueryString();
  
    setCurrentPage(urlCurrentPage);
    setItemsPerPage(take);
    setSearch(urlSearch);
    setAppliedFilters(urlFilters);
  
    _onReloadDataTable(urlFilters, urlSearch, skip, take);
  }, [locationSearch]);

  const handleExportSelectedData = async (orderId) => {
    handleDownloadPreOrder(orderId);
  };

  const handlePageChange = (event, page) => {
    const skip = (page - 1) * itemsPerPage;
    updateQueryStringInPreOrderList(skip, itemsPerPage);
  };

  const handleItemsPerPageChange = (event, newItemsPerPage) => {
    updateQueryStringInPreOrderList(0, newItemsPerPage);
  };

  const handleSearchOutput = (searchData) => {
    updateQueryStringInPreOrderList(0, itemsPerPage, searchData, appliedFilters);
  };

  const handleFiltersChange = (newFilters) => {
    const clonedFilters = cloneDeep(newFilters);
    updateQueryStringInPreOrderList(0, itemsPerPage, searchInitialState, clonedFilters);
  };

  const handleClearFilter = (handleReset) => {
    if (handleReset) handleReset();
    updateQueryStringInPreOrderList(
      0,
      itemsPerPage,
      search,
      preOrderFiltersInitialState
    );
  };

  const handleClearSearch = (handleReset) => {
    if (handleReset) handleReset();
    updateQueryStringInPreOrderList(
      0,
      itemsPerPage,
      searchInitialState,
      preOrderFiltersInitialState
    );
  };

  const onCloseDialog = () => {
    setIsCancelPreOrderDialog({
      id: null,
      preOrderId: null,
      open: false,
    });
  };

  const onCloseApproveDialog = () => {
    setIsApprovePreOrderDialog({
      id: null,
      orderId: null,
      open: false,
    });
  };

  const onCancelSelectedData = (selectedRows) => {
    if (handleResetCancelState) {
      handleResetCancelState();
    }
    const preOrderIds = selectedRows.map((row) => row?.data?.id);
    setIsCancelPreOrderDialog({
      id: id,
      preOrderId: preOrderIds,
      open: true,
    });
  };

  const onRequestAuthorList = async (data) => {
    try {
      setAuthorList((prev) => ({ ...prev, loading: true }));
      const {
        data: { list },
      } = await OrderService.fetchAuthorList(data);
      setAuthorList({ list, loading: false });
    } catch (e) {
      setAuthorList((prev) => ({ ...prev, loading: false }));
      dispatch(showErrorToast(e.message));
    }
  };

  const onApproveClick = () => {
    if (handleResetApprovalState) {
      handleResetApprovalState();
    }
    setIsApprovePreOrderDialog({
      id: id,
      orderId: id,
      open: true,
    });
  };

  return {
    loading,
    preOrderDetailData,
    preOrderResponse,
    orderData,
    currentPage,
    itemsPerPage,
    isCancelPreOrderDialog,
    isApprovePreOrderDialog,
    id,
    title,
    canApprove,
    canCancel,
    promotionalCicle,
    isAgent,
    isEmployee,
    appliedFilters,
    countAppliedFilters,
    showFilterIndicator,
    search,
    authorList,
    onRequestAuthorList,
    handleExportSelectedData,
    handlePageChange,
    handleItemsPerPageChange,
    handleSearchOutput,
    handleFiltersChange,
    handleClearFilter,
    handleClearSearch,
    onCloseDialog,
    onCloseApproveDialog,
    onCancelSelectedData,
    onApproveClick,
  };
};

export default usePreOrderList;
